import {
  ContractualScheduleEntry,
  ContractualWeeklySchedule,
  ContractualTimeSlot,
  ContractualDaySchedule,
  UserAssociation,
  User,
} from '../../../../api/users'
import { Organization } from '../../../../api/organizations'
import { dateToDateInputValue, dateInputToDateValue, startOfWeek } from '../../../../utils/date'
import { getWorkerAndOrganizationAssociation } from '../../../../utils/users'
import { isTimeSlotComplete } from './contractual-schedules-validation'

/**
 * Calculate total hours for a day's time slots
 * Duration is calculated as endHour - startHour
 * Handles slots that cross midnight (endHour < startHour => endHour + 24)
 */
export const calculateDayTotal = (timeSlots: ContractualTimeSlot[]): number => {
  return timeSlots.reduce((acc, slot) => {
    if (
      typeof slot.startHour !== 'number' ||
      typeof slot.endHour !== 'number' ||
      isNaN(slot.startHour) ||
      isNaN(slot.endHour)
    ) {
      return acc
    }

    let duration = slot.endHour - slot.startHour
    // Handle slots that cross midnight
    if (duration < 0) {
      duration = duration + 24
    }

    return acc + duration
  }, 0)
}

/**
 * Calculate total hours for all days of the week
 * Sums up all time slot durations across the week
 * Returns 0 if schedule is null or undefined
 */
export const calculateWeekTotals = (
  schedule: ContractualWeeklySchedule | null | undefined,
): number => {
  if (!schedule) {
    return 0
  }

  const daysOfWeek: (keyof ContractualWeeklySchedule)[] = [
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday',
  ]

  return daysOfWeek.reduce((acc, dayName) => {
    const timeSlots: ContractualTimeSlot[] = schedule[dayName]?.timeSlots || []
    const dayTotal = calculateDayTotal(timeSlots)
    return acc + dayTotal
  }, 0)
}

/**
 * Type for contractual schedule entry in form (with string dates for date inputs)
 */
export interface ContractualScheduleEntryFormValue {
  effectiveFrom: string // Format: YYYY-MM-DD (for HTML date input)
  effectiveTo: string | null // Format: YYYY-MM-DD or null
  schedule: ContractualWeeklySchedule // Toujours présent dans le formulaire (même si vide)
  weeklyHoursWorked: number | undefined // Heures hebdomadaires contractuelles (OBLIGATOIRE, undefined par défaut pour forcer la saisie)
  monthlyHoursWorked: number | undefined // Heures mensuelles contractuelles (OBLIGATOIRE, undefined par défaut pour forcer la saisie)
  useTimeSlots?: boolean // Utiliser les créneaux horaires (true par défaut)
}

/**
 * Transforms contractual schedules from API format (Date objects) to form format (string dates)
 * @param schedules - Contractual schedules from API
 * @returns Contractual schedules in form format
 */
export const transformContractualSchedulesFromAPI = (
  schedules?: ContractualScheduleEntry[],
): ContractualScheduleEntryFormValue[] => {
  if (!schedules || schedules.length === 0) {
    return []
  }

  return schedules.map(schedule => {
    // Vérifier si schedule existe et si des créneaux existent
    const hasTimeSlots =
      schedule.schedule !== null &&
      schedule.schedule !== undefined &&
      Object.values(schedule.schedule).some(
        daySchedule => daySchedule.timeSlots && daySchedule.timeSlots.length > 0,
      )

    // Si schedule est null, créer un schedule vide pour le formulaire
    const formSchedule = schedule.schedule || createEmptyWeeklySchedule()

    return {
      effectiveFrom: dateToDateInputValue(new Date(schedule.effectiveFrom)),
      effectiveTo: schedule.effectiveTo
        ? dateToDateInputValue(new Date(schedule.effectiveTo))
        : null,
      schedule: formSchedule,
      weeklyHoursWorked: schedule.weeklyHoursWorked ?? 0, // Obligatoire, défaut à 0
      monthlyHoursWorked: schedule.monthlyHoursWorked ?? 0, // Obligatoire, défaut à 0
      useTimeSlots: hasTimeSlots, // true si des créneaux existent, false sinon
    }
  })
}

/**
 * Transforms contractual schedules from form format (string dates) to API format (Date objects)
 * @param schedules - Contractual schedules from form
 * @returns Contractual schedules in API format
 */
/**
 * Filter out incomplete time slots (slots without both startHour and endHour)
 */
const filterCompleteTimeSlots = (daySchedule: ContractualDaySchedule): ContractualDaySchedule => {
  return {
    ...daySchedule,
    timeSlots: daySchedule.timeSlots.filter(isTimeSlotComplete),
  }
}

export const transformContractualSchedulesToAPI = (
  schedules: ContractualScheduleEntryFormValue[],
): ContractualScheduleEntry[] => {
  return schedules.map(schedule => {
    // Si useTimeSlots est false, envoyer schedule: null
    let scheduleToSend: ContractualWeeklySchedule | null = null

    if (schedule.useTimeSlots !== false && schedule.schedule) {
      // Filter out incomplete time slots before sending to API
      scheduleToSend = {
        monday: filterCompleteTimeSlots(schedule.schedule.monday),
        tuesday: filterCompleteTimeSlots(schedule.schedule.tuesday),
        wednesday: filterCompleteTimeSlots(schedule.schedule.wednesday),
        thursday: filterCompleteTimeSlots(schedule.schedule.thursday),
        friday: filterCompleteTimeSlots(schedule.schedule.friday),
        saturday: filterCompleteTimeSlots(schedule.schedule.saturday),
        sunday: filterCompleteTimeSlots(schedule.schedule.sunday),
      }
    }

    return {
      effectiveFrom: dateInputToDateValue(schedule.effectiveFrom),
      effectiveTo: schedule.effectiveTo ? dateInputToDateValue(schedule.effectiveTo) : null,
      schedule: scheduleToSend, // Peut être null
      weeklyHoursWorked: schedule.weeklyHoursWorked ?? 0, // Obligatoire
      monthlyHoursWorked: schedule.monthlyHoursWorked ?? 0, // Obligatoire
    }
  })
}

/**
 * Creates a default empty weekly schedule
 * Monday to Friday have one empty time slot by default
 * Saturday and Sunday have no time slots
 */
export const createEmptyWeeklySchedule = (): ContractualWeeklySchedule => {
  return {
    monday: { timeSlots: [createNewTimeSlot() as ContractualTimeSlot] },
    tuesday: { timeSlots: [createNewTimeSlot() as ContractualTimeSlot] },
    wednesday: { timeSlots: [createNewTimeSlot() as ContractualTimeSlot] },
    thursday: { timeSlots: [createNewTimeSlot() as ContractualTimeSlot] },
    friday: { timeSlots: [createNewTimeSlot() as ContractualTimeSlot] },
    saturday: { timeSlots: [] },
    sunday: { timeSlots: [] },
  }
}

/**
 * Creates a new contractual schedule entry with default values
 * @param effectiveFrom - Start date (defaults to today)
 * @returns New contractual schedule entry
 */
export const createNewContractualScheduleEntry = (
  effectiveFrom?: Date,
  weeklyHoursWorked?: number,
  monthlyHoursWorked?: number,
  useTimeSlots?: boolean,
): ContractualScheduleEntryFormValue => {
  const fromDate = effectiveFrom || new Date()
  return {
    effectiveFrom: dateToDateInputValue(startOfWeek(fromDate)),
    effectiveTo: null, // Active entry
    schedule: createEmptyWeeklySchedule(),
    weeklyHoursWorked: weeklyHoursWorked ?? undefined, // Obligatoire, undefined par défaut pour forcer la saisie
    monthlyHoursWorked: monthlyHoursWorked ?? undefined, // Obligatoire, undefined par défaut pour forcer la saisie
    useTimeSlots: useTimeSlots ?? true, // Par défaut, utiliser les créneaux horaires
  }
}

/**
 * Creates a new empty time slot
 * @returns New time slot with undefined startHour and endHour
 * Note: Using Partial to allow undefined values in form, backend expects numbers
 */
export const createNewTimeSlot = (): Partial<ContractualTimeSlot> => {
  // Empty time slot by default (use undefined for required fields so validation works)
  return {
    startHour: undefined,
    endHour: undefined,
  }
}

/**
 * Check if a worker has ever had non-empty time slots (from API data)
 * @param userAssociation - User association from API
 * @returns true if at least one period has at least one non-empty time slot
 */
export const hasWorkerEverHadTimeSlots = (userAssociation?: UserAssociation): boolean => {
  if (!userAssociation?.contractualSchedules || userAssociation.contractualSchedules.length === 0) {
    return false
  }

  // Check if any period has at least one day with at least one non-empty time slot
  return userAssociation.contractualSchedules.some(entry => {
    // Vérifier que schedule existe avant d'accéder aux timeSlots
    if (!entry.schedule) {
      return false
    }

    return Object.values(entry.schedule).some(daySchedule => {
      return (
        daySchedule.timeSlots &&
        daySchedule.timeSlots.length > 0 &&
        daySchedule.timeSlots.some(
          (slot: ContractualTimeSlot) =>
            typeof slot.startHour === 'number' &&
            !isNaN(slot.startHour) &&
            typeof slot.endHour === 'number' &&
            !isNaN(slot.endHour),
        )
      )
    })
  })
}

/**
 * Check if a contractual schedule can be applied to a worker
 * @param user - User to check
 * @param schedule - Contractual schedule entry to apply
 * @param organization - Organization (employer)
 * @returns Object with applicable boolean and optional reason
 */
export const canApplyContractualScheduleToWorker = (
  user: User,
  schedule: ContractualScheduleEntryFormValue,
  organization: Organization,
): { applicable: boolean; reason?: string } => {
  // Get worker association with the organization
  const association = getWorkerAndOrganizationAssociation(user, organization)

  // Check if worker has an active association
  if (!association) {
    return {
      applicable: false,
      reason: "Le travailleur n'a pas d'association active avec cet employeur",
    }
  }

  // Check if worker has any schedule entry with schedule (non-null)
  // If yes, NOT applicable for bulk modification
  // Rules for bulk modification:
  // - If worker has at least one schedule entry with schedule (non-null), NOT applicable
  // - If worker has no schedule entries with schedule (all null or none exist), can be replaced
  if (association.contractualSchedules) {
    const hasScheduleWithTimeSlots = association.contractualSchedules.some(entry => entry.schedule)

    if (hasScheduleWithTimeSlots) {
      return {
        applicable: false,
        reason: 'Le travailleur a déjà un horaire avec créneaux horaires détaillés',
      }
    }
  }

  return { applicable: true }
}
